import searchSlugUtils from 'app/shared/utils/searchSlugUtils';
import appUtils from 'app/shared/utils/appUtils';
import states from 'app/shared/utils/statesObject.json';
import { normalizeSlugToBaseRental } from 'app/shared/utils/slugNormalizationUtils';
import type { Breadcrumb } from 'app/types/breadcrumb.type';

// Type for the states JSON object
interface StateInfo {
  name: string;
  abbreviation: string;
  resourceId: string;
}

type StatesObject = Record<string, StateInfo>;

// Type for formatted breadcrumb
interface FormattedBreadcrumb {
  name: string;
  aria: string | null;
  uri: string;
}

// Cast the imported JSON to the correct type
const typedStates = states as StatesObject;

const getUSStateData = (areaId: string) => {
  // Check if the United Sates JSON object contains areaId
  const isInUnitedStatesObject = Object.prototype.hasOwnProperty.call(typedStates, areaId);
  const stateResourceId = isInUnitedStatesObject ? typedStates[areaId].resourceId : null;
  const stateName = isInUnitedStatesObject ? typedStates[areaId].name : null;
  const stateAbbreviation = isInUnitedStatesObject ? typedStates[areaId].abbreviation : null;

  return {
    stateResourceId,
    stateName,
    stateAbbreviation,
  };
};

const getLinkText = (breadcrumbArea: Breadcrumb): { linkText: string; ariaText?: string | null } => {
  if (breadcrumbArea.type === 'state') {
    const { stateAbbreviation, stateName } = getUSStateData(breadcrumbArea.id);

    if (stateAbbreviation) {
      // Use the resourceId is link text and the stateName is aria text
      return { linkText: stateAbbreviation, ariaText: stateName };
    }
  }
  return { linkText: breadcrumbArea.name };
};

const getAreaUri = (breadcrumbArea: Breadcrumb, searchSlug: string): string => {
  if (breadcrumbArea.type === 'state') {
    const { stateResourceId } = getUSStateData(breadcrumbArea.id);

    if (stateResourceId) {
      return '/' + stateResourceId;
    }
  }

  return '/' + breadcrumbArea.resourceId + '/' + searchSlug;
};

const formattedCrumb = (breadcrumbArea: Breadcrumb, searchSlug: string): FormattedBreadcrumb | false => {
  if (breadcrumbArea.type === 'metroarea') {
    return false;
  } else {
    const areaUri = getAreaUri(breadcrumbArea, searchSlug);
    const { linkText, ariaText = null } = getLinkText(breadcrumbArea);
    return {
      name: linkText,
      aria: ariaText,
      uri: areaUri,
    };
  }
};

const useBreadcrumbs = (
  areaCrumbs: Array<Breadcrumb> | undefined | null,
  currentListingCrumbs: Array<Breadcrumb> | undefined | null,
  searchSlug: string,
  variant: 'default' | 'baseRental' = 'default',
): [Array<FormattedBreadcrumb>] => {
  const crumbArray: Array<FormattedBreadcrumb> = [];

  // Use normalized slug for baseRental variant, otherwise use original slug
  const effectiveSlug = variant === 'baseRental' ? normalizeSlugToBaseRental(searchSlug) : searchSlug;

  const filterDescription = searchSlugUtils.prepareSlugDescription(effectiveSlug);

  const createCrumbs = (crumbs: Array<Breadcrumb>, slug: string): void => {
    crumbs.forEach((crumb) => {
      const createdCrumb = formattedCrumb(crumb, slug);
      if (createdCrumb) {
        crumbArray.push(createdCrumb);
      }
    });
  };

  if (currentListingCrumbs) {
    createCrumbs(currentListingCrumbs, effectiveSlug);
  } else if (Array.isArray(areaCrumbs)) {
    createCrumbs(areaCrumbs, effectiveSlug);
  }

  crumbArray.forEach((breadcrumb, i, arr) => {
    const remappedBreadcrumb = appUtils.remapPropertyLinks(breadcrumb) as FormattedBreadcrumb | undefined;
    if (remappedBreadcrumb) {
      arr[i] = remappedBreadcrumb;
    }

    // only add property type for last breadcrumb
    if (i === crumbArray.length - 1) {
      let appendText: string;

      if (variant === 'baseRental') {
        // For baseRental variant, use specific text based on original slug
        if (searchSlug === 'apartments-for-rent') {
          appendText = 'Apartments';
        } else if (searchSlug === 'houses-for-rent') {
          appendText = 'Houses';
        } else {
          appendText = 'Rentals';
        }
      } else {
        // Original logic for default variant
        const matchedText = filterDescription.match(/.+(?=for)/)?.[0];
        let hasWithAmenity = filterDescription.match(/(with).*$/)?.[0];

        if (!matchedText) {
          // default for slugs that start with 'for', ex: `for-rent-by-apartments`
          appendText = 'rentals';
        } else {
          appendText = matchedText;
        }

        appendText = appendText.trim();
        arr[i].name += ' ' + appendText;

        if (hasWithAmenity) {
          hasWithAmenity = hasWithAmenity.trim();
          arr[i].name += ' ' + hasWithAmenity;
        }
        return; // Skip the common append logic below
      }

      appendText = appendText.trim();
      arr[i].name += ' ' + appendText;
    }
  });

  return [crumbArray];
};

export default useBreadcrumbs;
